import type { z } from 'zod';

/** La respuesta no cumple el contrato. El mensaje solo lleva rutas y códigos, nunca los datos recibidos. */
export class ContractError extends Error {
  constructor(readonly issues: readonly { path: string; code: string }[]) {
    super(`La respuesta de la API no cumple el contrato: ${issues.map((issue) => `${issue.path || '(raíz)'} ${issue.code}`).join('; ')}`);
    this.name = 'ContractError';
  }
}

type Container = Record<PropertyKey, unknown>;

/**
 * Valida una respuesta con el esquema estricto de `packages/contracts` (plan 11 §4.3):
 * - `strict` (desarrollo y pruebas): cualquier diferencia, incluido un campo desconocido, es un error.
 * - Producción: un campo desconocido se elimina y la pantalla sigue (la API puede añadir campos antes de
 *   que se despliegue la web). Cualquier otra diferencia (campo que falta, tipo distinto) sigue siendo error.
 */
export function parseContract<T>(schema: z.ZodType<T>, data: unknown, strict: boolean): T {
  const result = schema.safeParse(data);
  if (result.success) return result.data;
  const issues = result.error.issues;
  if (!strict && issues.every((issue) => issue.code === 'unrecognized_keys')) {
    const copy: unknown = structuredClone(data);
    for (const issue of issues) {
      if (issue.code !== 'unrecognized_keys') continue;
      const target = issue.path.reduce<unknown>((node, key) => (node !== null && typeof node === 'object' ? (node as Container)[key] : undefined), copy);
      if (target !== null && typeof target === 'object') for (const key of issue.keys) delete (target as Container)[key];
    }
    const retry = schema.safeParse(copy);
    if (retry.success) return retry.data;
  }
  throw new ContractError(issues.map((issue) => ({ path: issue.path.map(String).join('.'), code: issue.code })));
}
